import React, { useEffect, useMemo, useState } from 'react';
import { Cog6ToothIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const INITIAL_FORM = {
  id: null,
  Content: '',
  Type: '',
  Sequence: 1,
  Live: 1,
  Subject: '',
  Link: '',
  CTA: '',
  TimeBetween: '',
};

const TYPE_COLORS = [
  'bg-indigo-50 border-indigo-200',
  'bg-emerald-50 border-emerald-200',
  'bg-amber-50 border-amber-200',
  'bg-sky-50 border-sky-200',
  'bg-purple-50 border-purple-200',
  'bg-rose-50 border-rose-200',
];

export default function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [modalState, setModalState] = useState({ open: false, mode: 'create' });
  const [formState, setFormState] = useState(INITIAL_FORM);
  const [showGenerator, setShowGenerator] = useState(false);
  const [personalisationLevel, setPersonalisationLevel] = useState(50);
  const [salesTemperature, setSalesTemperature] = useState(50);
  const [expandedIds, setExpandedIds] = useState([]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch('https://api.insightos.co.uk/sales/gettemplates', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to load templates');
      }

      const payload = await response.json();
      setTemplates(payload?.data ?? []);
    } catch (err) {
      console.error(err);
      setError('Unable to load email templates.');
    } finally {
      setLoading(false);
    }
  };

  const groupedTemplates = useMemo(() => {
    const map = new Map();
    templates.forEach((template) => {
      const typeKey = template.Type || 'Uncategorised';
      if (!map.has(typeKey)) {
        map.set(typeKey, []);
      }
      map.get(typeKey).push(template);
    });

    return Array.from(map.entries()).map(([type, items], index) => ({
      type,
      items: items
        .slice()
        .sort((a, b) => Number(a.Sequence ?? 0) - Number(b.Sequence ?? 0)),
      colorClass: TYPE_COLORS[index % TYPE_COLORS.length],
    }));
  }, [templates]);

  const openCreateModal = () => {
    setFormState(INITIAL_FORM);
    setModalState({ open: true, mode: 'create' });
    setShowGenerator(false);
    setFeedback('');
    setError('');
  };

  const openEditModal = (template) => {
    setFormState({
      id: template.id,
      Content: template.Content ?? '',
      Type: template.Type ?? '',
      Sequence: template.Sequence ?? 1,
      Live: template.Live ?? 1,
      Subject: template.Subject ?? '',
      Link: template.Link ?? '',
      CTA: template.CTA ?? '',
      TimeBetween:
        template.TimeBetween === null || template.TimeBetween === undefined
          ? ''
          : String(template.TimeBetween),
    });
    setModalState({ open: true, mode: 'edit' });
    setShowGenerator(false);
    setPersonalisationLevel(50);
    setSalesTemperature(50);
    setFeedback('');
    setError('');
  };

  const closeModal = () => {
    setModalState({ open: false, mode: 'create' });
    setFormState(INITIAL_FORM);
    setShowGenerator(false);
    setFeedback('');
    setError('');
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => {
      const isNumericField = name === 'Sequence' || name === 'Live' || name === 'CTA' || name === 'TimeBetween';
      const nextValue = isNumericField ? (value === '' ? '' : String(value)) : value;
      const nextState = {
        ...prev,
        [name]: nextValue,
      };

      if (name === 'Sequence') {
        const numericSequence = Number(value);
        if (!numericSequence || numericSequence < 2) {
          nextState.TimeBetween = '';
        }
      }

      return nextState;
    });
  };

  const getTemplateKey = (template) => {
    if (template?.id !== null && template?.id !== undefined) {
      return String(template.id);
    }
    return `${template?.Type ?? 'type'}-${template?.Sequence ?? 'seq'}`;
  };

  const toggleExpanded = (template) => {
    const key = getTemplateKey(template);
    setExpandedIds((prev) =>
      prev.includes(key) ? prev.filter((entry) => entry !== key) : [...prev, key]
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFeedback('');
    setError('');

    const timeBetweenValue = Number(formState.TimeBetween);

    const payload = {
      Content: formState.Content,
      Type: formState.Type,
      Sequence: Number(formState.Sequence) || 0,
      Live: Number(formState.Live) ?? 1,
      Subject: formState.Subject,
      Link: formState.Link,
      CTA: formState.CTA === '' ? null : (formState.CTA),
      TimeBetween:
        Number(formState.Sequence) >= 2
          ? formState.TimeBetween === '' || Number.isNaN(timeBetweenValue)
            ? null
            : timeBetweenValue
          : null,
    };

    try {
      const isEdit = modalState.mode === 'edit';
      const endpoint = isEdit
        ? `https://api.insightos.co.uk/sales/templates/${formState.id}`
        : 'https://api.insightos.co.uk/sales/templates';
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to save template');
      }

      setFeedback(isEdit ? 'Template updated.' : 'Template created.');
      await fetchTemplates();
      closeModal();
    } catch (err) {
      console.error(err);
      setError('Could not save template.');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateContent = async () => {
    setGenerating(true);
    setFeedback('');
    setError('');

    try {
      const response = await fetch('https://api.insightos.co.uk/sales/templategeneration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          type: formState.Type,
          sequence: Number(formState.Sequence) || 0,
          subject: formState.Subject,
          link: formState.Link,
          cta: formState.CTA,
          live: Number(formState.Live) ?? 1,
          personalisationLevel,
          salesTemperature,
          currentContent: formState.Content,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate content');
      }

      const templateText = await response.text();
      const contentValue = templateText?.trim();

      if (!contentValue) {
        throw new Error('No content returned from generator');
      }

      setFormState((prev) => ({
        ...prev,
        Content: contentValue,
      }));

      setFeedback('Generated content inserted.');
    } catch (err) {
      console.error(err);
      setError('Unable to generate content.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Email Templates</h2>
          <p className="mt-1 text-sm text-gray-600">
            Manage reusable outreach templates, sequences, and AI-assisted variations.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreateModal}
          className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
        >
          Create Template
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <ExclamationTriangleIcon className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow">
        {loading ? (
          <div className="px-6 py-10 text-center text-sm text-gray-500">Loading templates…</div>
        ) : groupedTemplates.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-gray-500">No templates found.</div>
        ) : (
          <>
            <div className="space-y-3 p-4 md:hidden">
              {groupedTemplates.map(({ type, items, colorClass }) => (
                <div key={type} className={`rounded-lg border p-3 ${colorClass}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-900">{type}</h3>
                    <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
                      {items.length} template{items.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="mt-3 space-y-3">
                    {items.map((template) => {
                      const templateKey = getTemplateKey(template);
                      const isExpanded = expandedIds.includes(templateKey);

                      return (
                        <div key={templateKey} className="rounded-md bg-white/70 p-3 shadow-sm">
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span className="font-semibold text-gray-900">Sequence {template.Sequence ?? '-'}</span>
                          <span>
                            {template.CreatedAt ? new Date(template.CreatedAt).toLocaleDateString() : '—'}
                          </span>
                        </div>
                        {Number(template.Sequence) >= 2 && (
                          <p className="mt-1 text-[11px] text-gray-500">
                            Time Between: {template.TimeBetween ?? '—'}
                          </p>
                        )}
                        <p className="mt-1 text-sm font-medium text-gray-900">{template.Subject ?? '—'}</p>
                        {template.Link && (
                          <a
                            href={template.Link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 block truncate text-xs text-indigo-600 hover:underline"
                          >
                            {template.Link}
                          </a>
                        )}
                        <div
                          className={`mt-2 whitespace-pre-wrap text-xs text-gray-600 ${
                            isExpanded ? '' : 'max-h-20 overflow-hidden'
                          }`}
                        >
                          {template.Content ?? ''}
                        </div>
                        {template.Content && template.Content.length > 120 && (
                          <button
                            type="button"
                            onClick={() => toggleExpanded(template)}
                            className="mt-2 text-xs font-semibold text-indigo-600 hover:underline"
                          >
                            {isExpanded ? 'Collapse' : 'Expand'}
                          </button>
                        )}
                        <div className="mt-3 flex items-center justify-between">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-semibold ${
                              Number(template.Live) === 1
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-gray-200 text-gray-600'
                            }`}
                          >
                            {Number(template.Live) === 1 ? 'Live' : 'Disabled'}
                          </span>
                          <button
                            type="button"
                            onClick={() => openEditModal(template)}
                            className="rounded-md border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden md:block">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-gray-600">Sequence</th>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-gray-600">Time Between</th>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-gray-600">Subject</th>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-gray-600">CTA</th>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-gray-600">Link</th>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-gray-600">Live</th>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-gray-600">Created</th>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-gray-600">Content Preview</th>
                      <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {groupedTemplates.map(({ type, items, colorClass }) => (
                      <React.Fragment key={type}>
                        <tr className={`border-y text-left text-gray-700 ${colorClass}`}>
                          <td colSpan={9} className="px-4 py-3 text-base font-semibold text-gray-900">
                            {type}
                            <span className="ml-3 text-xs font-medium uppercase tracking-wide text-gray-500">
                              {items.length} template{items.length === 1 ? '' : 's'}
                            </span>
                          </td>
                        </tr>
                        {items.map((template) => {
                          const templateKey = getTemplateKey(template);
                          const isExpanded = expandedIds.includes(templateKey);

                          return (
                            <tr key={templateKey} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium text-gray-900">{template.Sequence ?? '-'}</td>
                            <td className="px-4 py-3 text-gray-700">
                              {Number(template.Sequence) >= 2
                                ? template.TimeBetween ?? '—'
                                : '—'}
                            </td>
                            <td className="px-4 py-3 text-gray-700">{template.Subject ?? '—'}</td>
                            <td className="px-4 py-3 text-gray-700">{template.CTA ?? '—'}</td>
                            <td className="px-4 py-3 text-gray-700">
                              {template.Link ? (
                                <a
                                  href={template.Link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-indigo-600 hover:underline"
                                >
                                  {template.Link}
                                </a>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                                  Number(template.Live) === 1
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : 'bg-gray-200 text-gray-600'
                                }`}
                              >
                                {Number(template.Live) === 1 ? 'Live' : 'Disabled'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-500">
                              {template.CreatedAt ? new Date(template.CreatedAt).toLocaleString() : '—'}
                            </td>
                            <td className="px-4 py-3 text-gray-600">
                              <div
                                className={`whitespace-pre-wrap text-sm ${
                                  isExpanded
                                    ? 'max-h-64 overflow-y-auto pr-1'
                                    : 'max-h-16 max-w-xs overflow-hidden text-ellipsis'
                                }`}
                              >
                                {template.Content ?? ''}
                              </div>
                              {template.Content && template.Content.length > 120 && (
                                <button
                                  type="button"
                                  onClick={() => toggleExpanded(template)}
                                  className="mt-2 text-xs font-semibold text-indigo-600 hover:underline"
                                >
                                  {isExpanded ? 'Collapse' : 'Expand'}
                                </button>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <button
                                type="button"
                                onClick={() => openEditModal(template)}
                                className="inline-flex items-center rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
                              >
                                Edit
                              </button>
                            </td>
                          </tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {modalState.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-3 py-4 sm:px-4 sm:py-6">
          <div className="relative max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-xl bg-white shadow-2xl">
            <div className="sticky top-0 flex items-start justify-between border-b border-gray-200 bg-white px-4 py-3 sm:px-6 sm:py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {modalState.mode === 'edit' ? 'Edit Template' : 'Create Template'}
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  {modalState.mode === 'edit'
                    ? 'Update template details and regenerate content as needed.'
                    : 'Define a new email template and optionally generate content with AI.'}
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-full p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 px-4 py-4 sm:px-6 sm:py-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700" htmlFor="Type">Template Type</label>
                  <input
                    id="Type"
                    name="Type"
                    type="text"
                    required
                    value={formState.Type}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    placeholder="e.g. Beta Test"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700" htmlFor="Sequence">Sequence</label>
                  <input
                    id="Sequence"
                    name="Sequence"
                    type="number"
                    min="1"
                    required
                    value={formState.Sequence}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                {Number(formState.Sequence) >= 2 && (
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700" htmlFor="TimeBetween">Time Between</label>
                    <input
                      id="TimeBetween"
                      name="TimeBetween"
                      type="number"
                      min="0"
                      step="1"
                      required
                      value={formState.TimeBetween}
                      onChange={handleInputChange}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      placeholder="Enter delay between sends"
                    />
                  </div>
                )}
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700" htmlFor="Subject">Subject</label>
                  <input
                    id="Subject"
                    name="Subject"
                    type="text"
                    value={formState.Subject}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    placeholder="Subject line"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700" htmlFor="Link">Link</label>
                  <input
                    id="Link"
                    name="Link"
                    type="url"
                    value={formState.Link}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    placeholder="https://example.com"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700" htmlFor="CTA">CTA</label>
                  <input
                    id="CTA"
                    name="CTA"
                    type="text"
                    value={formState.CTA}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    placeholder="Optional CTA code"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700" htmlFor="Live">Status</label>
                  <select
                    id="Live"
                    name="Live"
                    value={formState.Live}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value={1}>Live</option>
                    <option value={0}>Disabled</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <label className="text-sm font-medium text-gray-700" htmlFor="Content">Content</label>
                  <button
                    type="button"
                    onClick={() => setShowGenerator((prev) => !prev)}
                    className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
                  >
                    <Cog6ToothIcon className="h-4 w-4" />
                    Generate
                  </button>
                </div>
                <textarea
                  id="Content"
                  name="Content"
                  rows={8}
                  required
                  value={formState.Content}
                  onChange={handleInputChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="Write or generate your email content here…"
                />
              </div>

              {showGenerator && (
                <div className="mx-auto max-w-lg rounded-lg border border-dashed border-indigo-200 bg-indigo-50 px-3 py-4 sm:px-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-indigo-700">
                    <Cog6ToothIcon className="h-5 w-5" />
                    AI Content Controls
                  </div>
                  <p className="mt-1 text-xs text-indigo-700/80">
                    Adjust the tone and temperature before generating fresh content.
                  </p>

                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <div>
                      <div className="flex justify-between text-xs font-semibold text-gray-600">
                        <span>Personalisation</span>
                        <span>{personalisationLevel}%</span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={personalisationLevel}
                        onChange={(event) => setPersonalisationLevel(Number(event.target.value))}
                        className="mt-2 w-full accent-indigo-600"
                      />
                      <div className="mt-1 flex justify-between text-[10px] uppercase tracking-wide text-gray-500">
                        <span>Professional</span>
                        <span>Personal</span>
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between text-xs font-semibold text-gray-600">
                        <span>Sales Temperature</span>
                        <span>{salesTemperature >= 66 ? 'Hot' : salesTemperature <= 33 ? 'Cold' : 'Warm'}</span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={salesTemperature}
                        onChange={(event) => setSalesTemperature(Number(event.target.value))}
                        className="mt-2 w-full accent-rose-500"
                      />
                      <div className="mt-1 flex justify-between text-[10px] uppercase tracking-wide text-gray-500">
                        <span>Cold</span>
                        <span>Warm</span>
                        <span>Hot</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      onClick={handleGenerateContent}
                      className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
                      disabled={generating}
                    >
                      {generating ? 'Generating…' : 'Generate Content'}
                    </button>
                  </div>
                </div>
              )}

              {feedback && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                  {feedback}
                </div>
              )}

              <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 disabled:cursor-not-allowed disabled:bg-indigo-400"
                >
                  {saving ? 'Saving…' : modalState.mode === 'edit' ? 'Update Template' : 'Create Template'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
